import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Mail, Send, Inbox, RefreshCw, Sparkles, Loader2, Eye, MessageSquare, Phone } from 'lucide-react';
import { toast } from 'sonner';

export default function MailClientPage() {
  const [tab, setTab] = useState('inbox');
  const [inbox, setInbox] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [smsInbox, setSmsInbox] = useState([]);
  const [smsOutbox, setSmsOutbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [aiReplying, setAiReplying] = useState(false);
  const [unread, setUnread] = useState(0);
  const [selectedMail, setSelectedMail] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeForm, setComposeForm] = useState({ to_email: '', subject: '', body: '' });
  const [sending, setSending] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [ib, ob, si, so] = await Promise.all([
        api.get('/mail/inbox'), api.get('/mail/outbox'),
        api.get('/sms/inbox'), api.get('/sms/outbox')
      ]);
      setInbox(ib.data.items);
      setOutbox(ob.data.items);
      setSmsInbox(si.data.items);
      setSmsOutbox(so.data.items);
      setUnread(ib.data.unread || 0);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFetch = async () => {
    setFetching(true);
    try {
      const r = await api.post('/mail/fetch');
      toast.success(`Pobrano ${r.data.fetched} emaili (usunietych z serwera: ${r.data.fetched})`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad pobierania'); }
    finally { setFetching(false); }
  };

  const handleAiReply = async (type) => {
    setAiReplying(true);
    try {
      const endpoint = type === 'email' ? '/mail/ai-auto-reply' : '/sms/ai-auto-reply';
      const r = await api.post(endpoint);
      const details = r.data.details || [];
      let msg = `AI: ${r.data.replied} odpowiedzi`;
      if (r.data.blacklisted) msg += `, ${r.data.blacklisted} wypisanych`;
      details.forEach(d => {
        if (d.action === 'replied') msg += `\n${d.email}: ${d.reply}`;
      });
      toast.success(msg, { duration: 8000 });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad AI'); }
    finally { setAiReplying(false); }
  };

  const openMail = async (id) => {
    try { const r = await api.get(`/mail/${id}`); setSelectedMail(r.data); fetchData(); }
    catch { toast.error('Blad'); }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await api.post('/mail/send', composeForm);
      toast.success('Email wyslany');
      setComposeOpen(false);
      setComposeForm({ to_email: '', subject: '', body: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad wysylki'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;

  return (
    <div className="space-y-6" data-testid="mail-client-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Poczta i SMS</h1>
          <p className="text-sm text-muted-foreground mt-1">Klient poczty, skrzynka odbiorcza i nadawcza, AI auto-odpowiedzi</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleFetch} disabled={fetching} data-testid="mail-fetch-button">
            {fetching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Pobierz poczte {unread > 0 && <Badge variant="destructive" className="ml-2 text-[10px] h-5">{unread}</Badge>}
          </Button>
          <Button onClick={() => setComposeOpen(true)} data-testid="mail-compose-button">
            <Send className="h-4 w-4 mr-2" />Napisz email
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-border/50 bg-card/80"><CardContent className="pt-4 pb-3 px-4 text-center">
          <p className="text-2xl font-semibold font-heading tabular-nums text-primary">{unread}</p>
          <p className="text-xs text-muted-foreground">Nieprzeczytane</p>
        </CardContent></Card>
        <Card className="border-border/50 bg-card/80"><CardContent className="pt-4 pb-3 px-4 text-center">
          <p className="text-2xl font-semibold font-heading tabular-nums">{inbox.length}</p>
          <p className="text-xs text-muted-foreground">Odebrane email</p>
        </CardContent></Card>
        <Card className="border-border/50 bg-card/80"><CardContent className="pt-4 pb-3 px-4 text-center">
          <p className="text-2xl font-semibold font-heading tabular-nums">{outbox.length}</p>
          <p className="text-xs text-muted-foreground">Wyslane email</p>
        </CardContent></Card>
        <Card className="border-border/50 bg-card/80"><CardContent className="pt-4 pb-3 px-4 text-center">
          <p className="text-2xl font-semibold font-heading tabular-nums">{smsInbox.length}</p>
          <p className="text-xs text-muted-foreground">Odebrane SMS</p>
        </CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="inbox"><Inbox className="h-3.5 w-3.5 mr-1.5" />Odebrane {unread > 0 && <Badge variant="destructive" className="ml-1.5 text-[10px] h-4 px-1.5">{unread}</Badge>}</TabsTrigger>
            <TabsTrigger value="outbox"><Send className="h-3.5 w-3.5 mr-1.5" />Wyslane</TabsTrigger>
            <TabsTrigger value="sms-in"><Phone className="h-3.5 w-3.5 mr-1.5" />SMS odebrane</TabsTrigger>
            <TabsTrigger value="sms-out"><MessageSquare className="h-3.5 w-3.5 mr-1.5" />SMS wyslane</TabsTrigger>
          </TabsList>
          {(tab === 'inbox' || tab === 'sms-in') && (
            <Button variant="outline" size="sm" onClick={() => handleAiReply(tab === 'inbox' ? 'email' : 'sms')} disabled={aiReplying}>
              {aiReplying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
              AI auto-odpowiedz
            </Button>
          )}
        </div>

        {/* Email Inbox */}
        <TabsContent value="inbox" className="mt-4">
          <Card className="border-border/50 bg-card/80">
            <CardContent className="p-0">
              {inbox.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground text-sm"><Inbox className="h-8 w-8 mx-auto mb-3 text-muted-foreground/30" />Skrzynka pusta. Kliknij "Pobierz poczte".</div>
              ) : (
                <div className="divide-y divide-border/30">
                  {inbox.map(m => (
                    <div key={m.id} onClick={() => openMail(m.id)} className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors duration-150 ${!m.is_read ? 'bg-primary/5' : ''}`}>
                      <div className={`w-2 h-2 rounded-full shrink-0 ${!m.is_read ? 'bg-primary' : 'bg-transparent'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm truncate ${!m.is_read ? 'font-semibold' : ''}`}>{m.contact_name || m.from_email}</span>
                          {m.ai_replied && <Badge variant="outline" className="text-[10px] text-primary shrink-0">AI</Badge>}
                          {m.ai_classification && <Badge variant="outline" className={`text-[10px] shrink-0 ${m.ai_classification === 'OPT_OUT' ? 'text-destructive' : m.ai_classification === 'INTERESTED' ? 'text-[hsl(var(--success))]' : m.ai_classification === 'SPAM' ? 'text-muted-foreground' : 'text-[hsl(var(--info))]'}`}>{m.ai_classification}</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{m.subject}</p>
                        {m.ai_reply_text && <p className="text-[10px] text-primary truncate mt-0.5">AI: {m.ai_reply_text.slice(0, 60)}...</p>}
                      </div>
                      <span className="text-[10px] text-muted-foreground shrink-0 font-mono">{m.created_at?.slice(0, 16).replace('T', ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Outbox */}
        <TabsContent value="outbox" className="mt-4">
          <Card className="border-border/50 bg-card/80">
            <CardContent className="p-0">
              {outbox.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground text-sm">Brak wyslanych emaili</div>
              ) : (
                <div className="divide-y divide-border/30">
                  {outbox.map(m => (
                    <div key={m.id} onClick={() => openMail(m.id)} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors duration-150">
                      <Send className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm truncate block">{m.contact_name || m.to_email}</span>
                        <p className="text-xs text-muted-foreground truncate">{m.subject}</p>
                      </div>
                      {m.ai_generated && <Badge variant="outline" className="text-[10px] text-primary shrink-0">AI</Badge>}
                      <span className="text-[10px] text-muted-foreground shrink-0 font-mono">{m.created_at?.slice(0, 16).replace('T', ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMS Inbox */}
        <TabsContent value="sms-in" className="mt-4">
          <Card className="border-border/50 bg-card/80">
            <CardContent className="p-0">
              {smsInbox.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground text-sm"><Phone className="h-8 w-8 mx-auto mb-3 text-muted-foreground/30" />Brak odebranych SMS. Bramki raportuja przychodzace przez API.</div>
              ) : (
                <div className="divide-y divide-border/30">
                  {smsInbox.map(m => (
                    <div key={m.id} className="px-4 py-3 hover:bg-muted/30 transition-colors duration-150">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium">{m.contact_name || m.phone}</span>
                        <span className="text-xs font-mono text-muted-foreground">{m.phone}</span>
                        {m.ai_replied && <Badge variant="outline" className="text-[10px] text-primary">AI</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground">{m.message}</p>
                      {m.ai_reply && <p className="text-xs text-primary mt-1 pl-3 border-l-2 border-primary/30">{m.ai_reply}</p>}
                      <span className="text-[10px] text-muted-foreground font-mono">{m.created_at?.slice(0, 16).replace('T', ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMS Outbox */}
        <TabsContent value="sms-out" className="mt-4">
          <Card className="border-border/50 bg-card/80">
            <CardContent className="p-0">
              {smsOutbox.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground text-sm">Brak wyslanych SMS</div>
              ) : (
                <div className="divide-y divide-border/30">
                  {smsOutbox.map(m => (
                    <div key={m.id} className="px-4 py-3 hover:bg-muted/30 transition-colors duration-150">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageSquare className="h-3.5 w-3.5 text-[hsl(var(--success))]" />
                        <span className="text-sm font-mono">{m.phone}</span>
                        <Badge variant="outline" className="text-[10px]">{m.status}</Badge>
                        {m.is_auto_reply && <Badge variant="outline" className="text-[10px] text-primary">Auto-AI</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{m.message?.slice(0, 100)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Mail Detail Sheet - Conversation Thread */}
      <Sheet open={!!selectedMail} onOpenChange={(open) => { if (!open) setSelectedMail(null); }}>
        <SheetContent className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading text-base">Konwersacja: {selectedMail?.contact_name || selectedMail?.from_email || selectedMail?.to_email}</SheetTitle>
            <SheetDescription>
              {selectedMail?.from_email !== selectedMail?.to_email ? (selectedMail?.direction === 'incoming' ? selectedMail?.from_email : selectedMail?.to_email) : ''}
              {selectedMail?.ai_classification && <Badge variant="outline" className="ml-2 text-[10px]">{selectedMail.ai_classification}</Badge>}
            </SheetDescription>
          </SheetHeader>
          {selectedMail && (
            <div className="space-y-3 mt-4">
              {/* Thread messages */}
              {(selectedMail.thread || [selectedMail]).map(m => (
                <div key={m.id} className={`p-3 rounded-lg border ${m.direction === 'outgoing' ? 'bg-primary/5 border-primary/20 ml-6' : 'bg-muted/30 border-border/30 mr-6'}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    {m.direction === 'incoming' ? <Inbox className="h-3 w-3 text-muted-foreground" /> : <Send className="h-3 w-3 text-primary" />}
                    <span className="text-xs font-medium">{m.direction === 'incoming' ? (m.from_email || '?') : (m.to_email || '?')}</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">{m.created_at?.slice(0, 16).replace('T', ' ')}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mb-1">{m.subject}</p>
                  {m.body_html && m.body_html.length > 20 ? (
                    <div className="text-xs prose prose-invert prose-xs max-w-none" dangerouslySetInnerHTML={{ __html: m.body_html.slice(0, 2000) }} />
                  ) : (
                    <p className="text-xs whitespace-pre-wrap">{(m.body_text || '').slice(0, 500)}</p>
                  )}
                  <div className="flex items-center gap-1.5 mt-2">
                    {m.ai_generated && <Badge className="text-[9px] bg-primary/10 text-primary border-primary/20 h-4 px-1.5">AI odpowiedz</Badge>}
                    {m.ai_classification && <Badge variant="outline" className="text-[9px] h-4 px-1.5">{m.ai_classification}</Badge>}
                    {m.campaign_id && <Badge variant="outline" className="text-[9px] h-4 px-1.5">Kampania</Badge>}
                  </div>
                </div>
              ))}
              {/* AI reply preview on incoming */}
              {selectedMail.direction === 'incoming' && selectedMail.ai_reply_text && (
                <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 ml-6">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Sparkles className="h-3 w-3 text-primary" />
                    <span className="text-[10px] font-medium text-primary">AI auto-odpowiedz</span>
                  </div>
                  <p className="text-xs whitespace-pre-wrap">{selectedMail.ai_reply_text}</p>
                </div>
              )}
              {/* Reply button */}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => {
                  const replyTo = selectedMail.direction === 'incoming' ? selectedMail.from_email : selectedMail.to_email;
                  setComposeForm({ to_email: replyTo, subject: `Re: ${selectedMail.subject}`, body: '' });
                  setComposeOpen(true); setSelectedMail(null);
                }}><Send className="h-3.5 w-3.5 mr-1.5" />Odpowiedz recznie</Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Nowy email</DialogTitle><DialogDescription>Wyslij wiadomosc email</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Do</Label><Input value={composeForm.to_email} onChange={e => setComposeForm({...composeForm, to_email: e.target.value})} placeholder="adres@email.pl" data-testid="mail-compose-to" /></div>
            <div className="space-y-2"><Label>Temat</Label><Input value={composeForm.subject} onChange={e => setComposeForm({...composeForm, subject: e.target.value})} data-testid="mail-compose-subject" /></div>
            <div className="space-y-2"><Label>Tresc</Label><Textarea value={composeForm.body} onChange={e => setComposeForm({...composeForm, body: e.target.value})} rows={8} data-testid="mail-compose-body" /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setComposeOpen(false)}>Anuluj</Button>
            <Button onClick={handleSend} disabled={sending || !composeForm.to_email || !composeForm.subject} data-testid="mail-compose-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}Wyslij
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
